/**
 * LibraryStep — new document, steps 3 and 4: first a screen of the sources
 * the chosen doctype usually draws on ("Sources this document draws on",
 * acknowledged before moving on), then "Which sources should the draft use?"
 * (design board 05).
 *
 * Shown in the doctype → region → sources → library → sections orchestration
 * in place of the Library Hub's library list. One radio per library in the
 * workspace, a chevron to look inside one (the hub's detail view) and a
 * "Start a new library" card. The sources screen is skipped when the list is
 * empty or will not load, and when no loader is given (building on an
 * existing document).
 *
 * SRP: pure presentation + the expected-sources fetch. The shell decides what
 * Use / Back / Cancel / Upload do.
 */

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { ExpectedSource } from "../../api/client";
import { Button, ChoiceList, Eyebrow, LinkButton, SearchField, StepHeader } from "../../ds/components";
import type { Library } from "../../types";
import ExpectedSources from "./ExpectedSources";

export interface LibraryStepProps {
  open: boolean;
  libraries: readonly Library[];
  /** Pre-selected library (the one already in use), if any. */
  selectedLibraryId?: string | null;
  workspaceName?: string | null;
  /** Doctype name and backend code, for the expected-sources disclosure. */
  docName?: string | null;
  docType?: string | null;
  loadExpectedSources?: (docType: string) => Promise<ExpectedSource[]>;
  /** Steps in the flow: 5 for a new document (doctype, region, sources,
   *  library, sections); 3 when building on an existing document. */
  stepTotal?: number;
  onUse: (library: Library) => void;
  onInspect?: (library: Library) => void;
  onNewLibrary?: () => void;
  /** Building on an existing document: the library is optional. */
  onSkip?: () => void;
  onBack: () => void;
  onCancel: () => void;
}

/** "10 files", "1 file · 2 to review". Pure. */
export function libraryMeta(lib: Library): string {
  const n = lib.files ?? lib.document_count;
  const parts: string[] = [];
  if (typeof n === "number") parts.push(`${n} ${n === 1 ? "file" : "files"}`);
  if (lib.unverified) parts.push(`${lib.unverified} to review`);
  return parts.join(" · ");
}

export const LibraryStep: React.FC<LibraryStepProps> = ({
  open,
  libraries,
  selectedLibraryId = null,
  workspaceName,
  docName,
  docType,
  loadExpectedSources,
  stepTotal = 5,
  onUse,
  onInspect,
  onNewLibrary,
  onSkip,
  onBack,
  onCancel,
}) => {
  const [picked, setPicked] = useState<string | null>(selectedLibraryId);
  const [query, setQuery] = useState("");
  const [sources, setSources] = useState<ExpectedSource[] | null>(null);
  const [sourcesFailed, setSourcesFailed] = useState(false);
  // The writer has read the sources screen and moved on to the libraries.
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(selectedLibraryId || null);
    setQuery("");
    setAcknowledged(false);
    // Re-seeded on open only.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  }, [open]);

  // The usual sources for this doctype. A list that will not load (or a
  // doctype that declares none) simply leaves the disclosure out: it must
  // never stand between a writer and a draft.
  useEffect(() => {
    if (!open || !docType || !loadExpectedSources) return;
    let alive = true;
    setSources(null);
    setSourcesFailed(false);
    loadExpectedSources(docType).then(
      (list) => {
        if (alive) setSources(list);
      },
      () => {
        if (alive) setSourcesFailed(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [open, docType, loadExpectedSources]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? libraries.filter((l) => l.name.toLowerCase().includes(q)) : libraries;
  }, [libraries, query]);

  if (!open) return null;

  const chosen = libraries.find((l) => l.id === picked) ?? null;
  // The sources screen counts as a step whenever it is planned (a loader and
  // a doctype); a failed or empty list skips it without renumbering.
  const sourcesPlanned = !!loadExpectedSources && !!docType;
  const sourcesSkipped = sourcesFailed || (sources !== null && sources.length === 0);
  const showSourcesScreen = sourcesPlanned && !sourcesSkipped && !acknowledged;
  const libraryStepNo = sourcesPlanned ? 4 : 3;

  if (showSourcesScreen) {
    return (
      <div className="as-takeover" role="dialog" aria-modal="true" aria-label="Sources this document draws on">
        <StepHeader
          onBack={onBack}
          step={`New document · step 3 of ${stepTotal}`}
          title="Sources this document draws on"
        />
        <div className="as-scroll as-sunken as-takeover-body as-stack">
          <ExpectedSources sources={sources} docName={docName} />
        </div>
        <div className="as-footer">
          <div className="as-footer-actions">
            <Button variant="secondary" onClick={onBack}>
              Back
            </Button>
            <Button variant="primary" disabled={sources === null} onClick={() => setAcknowledged(true)}>
              Acknowledge
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="as-takeover" role="dialog" aria-modal="true" aria-label="Which sources should the draft use?">
      <StepHeader
        // Back returns to the sources screen when it was shown.
        onBack={sourcesPlanned && !sourcesSkipped ? () => setAcknowledged(false) : onBack}
        step={`New document · step ${libraryStepNo} of ${stepTotal}`}
        title="Which sources should the draft use?"
      >
        <p className="as-p">
          Pick the library that holds this study's protocol, SAP and TFLs. Every claim in the draft cites a page from
          it.
        </p>
        <SearchField value={query} onChange={setQuery} placeholder="Search libraries" />
      </StepHeader>

      <div className="as-scroll as-sunken as-takeover-body as-stack">
        <Eyebrow>{workspaceName ? `Source libraries · ${workspaceName}` : "Source libraries"}</Eyebrow>
        {shown.length > 0 ? (
          <ChoiceList
            name="library"
            label="Source library"
            value={picked}
            onChange={setPicked}
            options={shown.map((lib) => ({
              id: lib.id,
              label: lib.name,
              meta: libraryMeta(lib) || undefined,
              onMore: onInspect ? () => onInspect(lib) : undefined,
              moreLabel: `Look inside ${lib.name}`,
            }))}
          />
        ) : (
          <p className="as-note">
            {libraries.length === 0 ? "No libraries in this workspace yet." : `No library matches “${query}”.`}
          </p>
        )}

        {onNewLibrary && (
          <div className="as-dashed as-stack-8">
            <b>Start a new library</b>
            <span className="as-note">Add files from your computer.</span>
            <div>
              <Button variant="secondary" size="sm" onClick={onNewLibrary}>
                Upload
              </Button>
            </div>
          </div>
        )}

        {onSkip && (
          <div>
            <LinkButton onClick={onSkip}>Continue without a library</LinkButton>
          </div>
        )}
      </div>

      <div className="as-footer">
        <div className="as-footer-actions">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!chosen}
            onClick={() => chosen && onUse(chosen)}
            // The chosen row is highlighted, so the label stays short; a long
            // library name would not fit the pane. The name is still read out.
            aria-label={chosen ? `Use ${chosen.name}` : undefined}
            title={chosen ? `Use ${chosen.name}` : undefined}
          >
            {chosen ? "Use this library" : "Use library"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LibraryStep;
